import { sanitizeEnvelope } from "./telemetry-sanitizer.js";

//#region src/web/telemetry-drops.ts
/** Name of the metric that reports dropped envelopes. */
const DROP_METRIC_NAME = "telemetrySanitizerDrop";
const UNKNOWN_DROP_DIMENSION = "unknown";
const scheduleWithTimeout = (task) => {
	setTimeout(task, 0);
};
/**
* Creates a reporter that batches drop counts and sends one metric per
* distinct (reason, baseType, errorName) from a deferred task. `record` never sends directly, because
* sending from inside a telemetry initializer would re-enter it.
*
* Only one flush is scheduled per batch. A `record` made while the flush is
* sending joins the batch being sent and never schedules another flush, so a
* failing send can't loop.
*
* @param send Reports one combination's count; may throw, the batch is still cleared.
* @param schedule Defers the flush; defaults to `setTimeout(task, 0)`.
*/
function createDropReporter(send, schedule = scheduleWithTimeout) {
	const counts = /* @__PURE__ */ new Map();
	let sending = false;
	const flush = () => drain(counts, send, (value) => {
		sending = value;
	});
	return {
		record: (reason, envelope, error) => {
			if (startsBatch(counts, toDropProperties(reason, envelope, error))) schedule(flush);
		},
		isSending: () => sending
	};
}
/**
* Wraps a telemetry initializer so that any exception becomes a `false`
* return, the one result that stops an envelope from being sent.
*
* @param record Notified with `'initializer-error'`, the envelope and the caught error on a throw.
* @param initializer The initializer to guard; its result passes through.
*/
function failClosed(record, initializer) {
	return (envelope) => {
		try {
			return initializer(envelope);
		} catch (error) {
			record("initializer-error", envelope, error);
			return false;
		}
	};
}
/**
* Sanitizes the envelope in place, reporting failure as `false` (and a
* `'sanitizer-error'` drop) instead of throwing.
*
* @param envelope The telemetry item to sanitize.
* @param record Notified with `'sanitizer-error'`, the envelope and the caught error on failure.
* @returns true when the envelope may be sent, false when it must be dropped.
*/
function sanitizeOrDrop(envelope, record) {
	try {
		sanitizeEnvelope(envelope);
		return true;
	} catch (error) {
		record("sanitizer-error", envelope, error);
		return false;
	}
}
function toDropProperties(reason, envelope, error) {
	return {
		reason,
		baseType: envelope.baseType ?? UNKNOWN_DROP_DIMENSION,
		errorName: error instanceof Error ? error.name : UNKNOWN_DROP_DIMENSION
	};
}
function drain(counts, send, setSending) {
	setSending(true);
	try {
		counts.forEach(({ properties, count }) => send(DROP_METRIC_NAME, count, properties));
	} finally {
		setSending(false);
		counts.clear();
	}
}
function startsBatch(counts, properties) {
	const isFirst = counts.size === 0;
	const key = JSON.stringify(properties);
	counts.set(key, {
		properties,
		count: (counts.get(key)?.count ?? 0) + 1
	});
	return isFirst;
}

//#endregion
export { createDropReporter, failClosed, sanitizeOrDrop };